import { afterNextRender, Component, computed, inject, input, signal, Signal, WritableSignal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SignalsStoreService } from '../../signals-store.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { daysOfWeek, formatDateToReadableString, formatDateToYYYYMMDD, formatSubscriptionDate, getDateFromDayAndWeek, getWeekNumberFromDate } from '../../utils/formatting';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ProductSubscription } from '../modal-content';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MatInputModule } from '@angular/material/input';
import { Item } from '../../types/common.types';
import { FormattedDeliveryInformation, TemporaryRouteChange } from '../../../settings/account/deliveries/intarfaces';
import { Session } from '../../types/session.type';
import { LocalStorageService } from '../../local-storage.service';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { DeliveriesService } from '../../../settings/account/deliveries/deliveries.service';

@Component({
  selector: 'app-product-subscription',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    CommonModule,
    MatDatepickerModule,
    EmptyMessageComponent
  ],
  templateUrl: './product-subscription.component.html',
  styleUrl: './product-subscription.component.scss'
})
export class ProductSubscriptionComponent {

  //#region Services

  #activeModal = inject(NgbActiveModal);

  #signalsStoreService = inject(SignalsStoreService);

  #localStorageService = inject(LocalStorageService);

  #deliveriesService = inject(DeliveriesService);

  //#endregion

  //#region Inputs / Outputs

  productSubsInfo = input.required<ProductSubscription | undefined>();

  //#endregion

  //#region Properties

  currentDate = new Date();

  selectedDateSubscription!: any;

  selectedFrequency!: Item | undefined;

  frequencies: Signal<any[]> = computed(() => {
    let freqs = [];
    // If the selected attribute has customFrequencies:
    const sa = this.productSubsInfo()?.selectedAttributeCardConfig;

    // if the selected attribute confirm its a variant:
    if (sa.variantId) {
      if (sa.useCustomFrequencies === false) {
        // We should use the global frequencies if the attribute specifically says not to use it:
        freqs = this.#signalsStoreService.frequencies() || [];
      } else if (sa.useCustomFrequencies) {
        // Use the attribute custom frequencies
        freqs = sa.customFrequencies;
      }
    } else {
      if (this.productSubsInfo()?.useCustomFrequencies)
        freqs = this.productSubsInfo()?.customFrequencies ?? [];
      else
        freqs = this.#signalsStoreService.frequencies() || [];
    }


    if (freqs.length) {
      this.selectedFrequency = freqs[0];
    }

    return freqs;
  });

  //#endregion

  //#region Signals:
  deliveryDay: WritableSignal<string> = signal('');
  minDate: WritableSignal<Date> = signal(new Date());
  isTemporaryRouteChange: WritableSignal<boolean> = signal(false);
  initiateSubscriptionImmediatly: WritableSignal<boolean> = signal(true);
  weekTemporaryRouteChanges: WritableSignal<number[]> = signal([]);
  currentTemporaryRouteChangeInfo: WritableSignal<TemporaryRouteChange | null> = signal(null);
  temporaryRouteChangeCustomMessage: WritableSignal<string> = signal('');

  //#endregion

  deliveryZoneInfo: Signal<FormattedDeliveryInformation | null> = computed(() => this.#deliveriesService.deliveryZoneInfo());

  constructor() {
    afterNextRender(() => this.#validateTemporaryRoute());
  }

  //#region Methods

  onDateChange(event: any) {
    const selectedDate: Date = event.value;
    const formattedDate: string = formatSubscriptionDate(selectedDate);

    this.selectedDateSubscription = formattedDate;
  }

  dateFilter = (date: Date | null) => {
    if (!date) {
      return false;
    }

    if (!this.deliveryDay()) return true;

    const numberWeek = getWeekNumberFromDate(date);

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const index = daysOfWeek.indexOf(this.deliveryDay());

    // If the date is in the week of route temporary change, disable it.
    if (this.weekTemporaryRouteChanges() && this.weekTemporaryRouteChanges().includes(numberWeek))
      return false;

    return date.getDay() === index;
  }

  cancel = () => this.#activeModal.close({ isCancel: true });

  continue = () => this.#activeModal.close({
    isCancel: false,
    isContinue: true,
    initiateSubscriptionImmediatly: this.initiateSubscriptionImmediatly(),
    selectedDateSubscription: this.selectedDateSubscription,
    selectedFrequency: this.selectedFrequency
  });

  subscribe = (event: Event) => {

    this.#activeModal
      .close({
        isCancel: false,
        event,
        initiateSubscriptionImmediatly: this.initiateSubscriptionImmediatly(),
        selectedDateSubscription: this.selectedDateSubscription,
        selectedFrequency: this.selectedFrequency
      });
  }

  onChangeFrequency(target: any) {
    const frequencyId = target.value
    this.selectedFrequency = this.frequencies().find(frequency => frequency.id === frequencyId);
  }

  #validateTemporaryRoute() {
    const currentSessionInfo: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);

    if (!currentSessionInfo)
      return;

    const { temporaryChange, upcomingTemporaryChanges } = currentSessionInfo?.deliveryInfo ?? {};

    this.isTemporaryRouteChange.set(!!temporaryChange?.active);
    const weekTemporaryRouteChanges = [...(upcomingTemporaryChanges?.map(e => e.weekNumber) ?? []), temporaryChange?.weekNumber ?? 0];
    this.currentTemporaryRouteChangeInfo.set(temporaryChange ?? null);
    this.weekTemporaryRouteChanges.set(weekTemporaryRouteChanges);
    if (!this.productSubsInfo()?.subscribeAndcustomizeBox)
      this.initiateSubscriptionImmediatly.set(!this.isTemporaryRouteChange());
    else
      this.initiateSubscriptionImmediatly.set(false);

    this.#setUpTemporaryRouteChangeCustomMessage();

    this.#setUpMinDateForSubscription();
  }

  #setUpMinDateForSubscription() {
    const session: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    if (session?.deliveryInfo) {
      this.deliveryDay.set(session.deliveryInfo.subscriptionDeliveryDay);

      const currentTemporaryRouteChangeInfo = this.currentTemporaryRouteChangeInfo();
      const dayNumber = daysOfWeek.indexOf(session.deliveryInfo.subscriptionDeliveryDay.toLowerCase());
      if (this.isTemporaryRouteChange() && currentTemporaryRouteChangeInfo) {
        const nextWeek = currentTemporaryRouteChangeInfo.weekNumber + 1;
        // Calculate the date for the next week on the specified delivery day
        this.minDate.set(getDateFromDayAndWeek(dayNumber, nextWeek));
      } else {
        // get week number from current subscription date
        const weekNumber = getWeekNumberFromDate(new Date(session.deliveryInfo.deliveryDate));
        this.minDate.set(getDateFromDayAndWeek(dayNumber, weekNumber));
      }
    }
  }

  #setUpTemporaryRouteChangeCustomMessage() {
    const currentSessionInfo: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    const currentTemporaryRouteChangeInfo = this.currentTemporaryRouteChangeInfo();
    if (!currentSessionInfo?.deliveryInfo || !currentTemporaryRouteChangeInfo?.active)
      return;

    const subscriptionDateNumber = daysOfWeek.indexOf(currentSessionInfo.deliveryInfo.subscriptionDeliveryDay.toLowerCase());
    const subscriptionDate = getDateFromDayAndWeek(subscriptionDateNumber, currentTemporaryRouteChangeInfo.weekNumber);

    // parse to this format: 2025-11-21
    const formattedAuxDate = formatDateToYYYYMMDD(subscriptionDate);
    const formattedDate = formatDateToReadableString(formattedAuxDate).mmddyyyyFormat;
    this.temporaryRouteChangeCustomMessage.set(`Due to holidays or delivery changes the delivery for <strong>${formattedDate}</strong> is not available.`);
  }

  //#endregion
}
